import { formatPhone } from '../../shared/phone';
import type {
  ConversationStatus,
  SmsConversationListItem,
  SmsMessage,
} from '../../types/messaging';
import type { Delivery, ThreadMessage, Tone } from '../../ui/v2';

/**
 * Views are the questions an operator asks, not the status enum:
 *   inbox    — not closed (OPEN + PENDING), whoever is answering
 *   needs    — Arthur handed off (`needs_human`), not closed
 *   closed   — closed
 *   all      — everything that isn't put away
 *   archived — put away by staff, whatever its status
 * "Pending" is a row word ("Waiting on guest"), not a view.
 */
export type View = 'inbox' | 'needs' | 'closed' | 'all' | 'archived';
export const VIEWS: View[] = ['inbox', 'needs', 'closed', 'all', 'archived'];
export const VIEW_LABEL: Record<View, string> = {
  inbox: 'Inbox',
  needs: 'Needs you',
  closed: 'Closed',
  all: 'All',
  archived: 'Archived',
};

export interface ListFilter {
  status?: ConversationStatus;
  needs_human?: boolean;
  archived?: boolean;
}

/**
 * The list endpoint takes one `status`; "not closed" is two requests merged
 * until the API grows an `is_closed` filter (SMS_V2_SPEC §10 #2).
 */
export const VIEW_FILTERS: Record<View, ListFilter[]> = {
  inbox: [
    { status: 'OPEN', archived: false },
    { status: 'PENDING', archived: false },
  ],
  needs: [
    { status: 'OPEN', needs_human: true, archived: false },
    { status: 'PENDING', needs_human: true, archived: false },
  ],
  closed: [{ status: 'CLOSED', archived: false }],
  all: [{ archived: false }],
  archived: [{ archived: true }],
};

/**
 * Whether putting a conversation away takes it out of the view it was
 * opened from, so the detail should go with it. Bringing one back (reopen,
 * restore) never does: the user is about to reply, and the panel stays.
 */
export function leavesView(view: View, action: 'close' | 'archive'): boolean {
  return action === 'close'
    ? view === 'inbox' || view === 'needs'
    : view !== 'archived';
}

/** `?view=`, falling back to the legacy `?status=` links. */
export function viewFromParams(params: URLSearchParams): View {
  const view = params.get('view') as View | null;
  if (view && VIEWS.includes(view)) return view;
  return params.get('status')?.toUpperCase() === 'CLOSED' ? 'closed' : 'inbox';
}

// ---------------------------------------------------------------------------
// Who is answering
// ---------------------------------------------------------------------------

export type AnsweringKind =
  | 'arthur'
  | 'needs'
  | 'staff'
  | 'guest'
  | 'off'
  | 'closed';

export interface Answering {
  kind: AnsweringKind;
  label: string;
  tone: Tone;
  hollow?: boolean;
  /** Assigned to the signed-in user. */
  mine?: boolean;
}

export interface Me {
  id?: number | null;
  email?: string | null;
}

type AnsweringFields = Pick<
  SmsConversationListItem,
  | 'is_closed'
  | 'status'
  | 'needs_human'
  | 'assigned_to'
  | 'assigned_to_email'
  | 'agent_status'
>;

/**
 * One rule: accent = Arthur has it, warning = needs a person, success = a
 * person has it, neutral = nothing to do. Order matters — a closed thread is
 * closed whoever had it; a hand-off outranks Arthur being off.
 */
export function answering(c: AnsweringFields, me?: Me | null): Answering {
  if (c.is_closed || c.status === 'CLOSED')
    return { kind: 'closed', label: 'Closed', tone: 'neutral' };
  if (c.needs_human && !c.assigned_to)
    return {
      kind: 'needs',
      label: 'Waiting for you',
      tone: 'warning',
      hollow: true,
    };
  if (c.assigned_to) {
    const mine =
      (me?.id != null && c.assigned_to === me.id) ||
      (!!me?.email && c.assigned_to_email === me.email);
    return {
      kind: 'staff',
      label: mine
        ? 'You’re answering'
        : `${shortName(c.assigned_to_email)} is answering`,
      tone: 'success',
      mine,
    };
  }
  if (c.agent_status === 'DISABLED')
    return {
      kind: 'off',
      label: 'Arthur is off for texts',
      tone: 'neutral',
      hollow: true,
    };
  if (c.status === 'PENDING')
    return {
      kind: 'guest',
      label: 'Waiting on guest',
      tone: 'neutral',
      hollow: true,
    };
  return { kind: 'arthur', label: 'Arthur is answering', tone: 'accent' };
}

/**
 * States a list row leaves unsaid. "Off" is the default for every thread
 * while the SMS agent is not replying, so naming it on each row is noise;
 * the detail's handoff row still says it.
 */
export const QUIET_ON_ROW: AnsweringKind[] = ['arthur', 'off', 'closed'];

export function handoffSentence(a: Answering): string {
  switch (a.kind) {
    case 'arthur':
      return 'Replies go out as Arthur. Take over to answer yourself.';
    case 'needs':
      return 'Arthur handed this off. Take it to answer, or close it if nothing is left.';
    case 'staff':
      return a.mine
        ? 'Assigned to you. Hand it back when you’re done.'
        : 'Assigned to a colleague. You can still read and reply.';
    case 'guest':
      return 'Arthur asked a question and is waiting for the guest to reply.';
    case 'off':
      return 'Arthur doesn’t reply on this thread. Replies are yours.';
    case 'closed':
      return 'Closed conversations stay readable. Reopen to continue.';
  }
}

// ---------------------------------------------------------------------------
// Display
// ---------------------------------------------------------------------------

/** "dana.kessler@…" → "Dana Kessler". */
export function shortName(email: string | null | undefined): string {
  if (!email) return 'Staff';
  return email
    .split('@')[0]
    .split(/[._-]/)
    .filter(Boolean)
    .map((p) => p[0].toUpperCase() + p.slice(1))
    .join(' ');
}

export function displayName(
  c: Pick<SmsConversationListItem, 'customer_name' | 'customer_phone_e164'>,
): string {
  return (
    c.customer_name?.trim() || formatPhone(c.customer_phone_e164) || 'Unknown'
  );
}

export function firstName(name: string | null | undefined): string | null {
  return name?.trim().split(/\s+/)[0] || null;
}

/** Last two digits of a number, for an unnamed guest's avatar. */
export function phoneGlyph(phone: string | null | undefined): string {
  return (phone || '').replace(/\D/g, '').slice(-2) || '?';
}

const DELIVERY: Partial<Record<SmsMessage['status'], Delivery>> = {
  QUEUED: 'queued',
  SENT: 'sent',
  DELIVERED: 'delivered',
  FAILED: 'failed',
};

/**
 * Messages as thread turns. Inbound is the guest; outbound is Arthur when
 * the agent sent it, otherwise the staff member who did. System rows are
 * not part of the conversation.
 */
export function toThread(
  messages: SmsMessage[],
  guestName?: string | null,
): ThreadMessage[] {
  return messages
    .filter((m) => m.message_type !== 'SYSTEM')
    .map((m) => {
      const inbound = m.direction === 'INBOUND';
      return {
        id: m.id,
        direction: inbound ? 'in' : 'out',
        author: inbound
          ? { kind: 'guest', name: guestName || null }
          : m.sent_by_agent
            ? { kind: 'arthur' }
            : { kind: 'staff', name: shortName(m.sent_by_user_email) },
        body: m.body,
        at: m.created_at,
        delivery: inbound ? null : (DELIVERY[m.status] ?? null),
        error:
          m.status === 'FAILED'
            ? m.error_message || 'The carrier rejected the message.'
            : null,
      };
    });
}
